/**
 * The Solstack mark: a bold "S" on a blue square, drawn the way the site
 * draws everything (src/style/theme.css): a black rule, a small radius and
 * a hard black shadow offset down and to the right. On a 64×64 grid; at the
 * site's usual 30px the rule comes out near 2px and the shadow near 4px,
 * as on the site's cards and buttons. Blue (--accent-2), not the lime the
 * buttons are filled with, so the logo never reads as one more button.
 *
 * The one source for every copy of the mark: the site (Logo.js), the
 * favicon, app icons and public/logo files (scripts/brand-icons.mjs), the
 * link-preview cards (scripts/og-cards.mjs), the Token Inspector report
 * image (src/solstack/lib/reportSvg.js) and the Telegram bot's scan card
 * (worker/scanCard.js).
 *
 * Dependency-free on purpose: the worker and the Node scripts import it too.
 */

export const MARK_GRID = 64;

// DM Sans ExtraBold "S", outlined at a 28-unit cap height and centred in
// the square (its centre is 28, 28), so no font is needed to draw it.
const S_PATH =
  "M28.34 42.48Q25.30 42.48 22.92 41.44Q20.54 40.40 19.14 38.40Q17.74 36.40 17.70 33.56L23.62 33.56Q23.66 34.76 24.24 35.72Q24.82 36.68 25.86 37.24Q26.90 37.80 28.30 37.80Q29.54 37.80 30.44 37.40Q31.34 37 31.84 36.28Q32.34 35.56 32.34 34.52Q32.34 33.40 31.76 32.60Q31.18 31.80 30.18 31.24Q29.18 30.68 27.88 30.22Q26.58 29.76 25.10 29.28Q21.78 28.20 20.04 26.40Q18.30 24.60 18.30 21.60Q18.30 19.04 19.52 17.24Q20.74 15.44 22.92 14.48Q25.10 13.52 27.86 13.52Q30.70 13.52 32.86 14.50Q35.02 15.48 36.28 17.32Q37.54 19.16 37.62 21.68L31.62 21.68Q31.58 20.76 31.10 19.98Q30.62 19.20 29.78 18.72Q28.94 18.24 27.78 18.24Q26.78 18.20 25.96 18.54Q25.14 18.88 24.66 19.54Q24.18 20.20 24.18 21.20Q24.18 22.20 24.66 22.86Q25.14 23.52 26 24Q26.86 24.48 28.02 24.90Q29.18 25.32 30.54 25.76Q32.66 26.48 34.42 27.46Q36.18 28.44 37.24 30Q38.30 31.56 38.30 34.12Q38.30 36.36 37.14 38.26Q35.98 40.16 33.78 41.32Q31.58 42.48 28.34 42.48Z";

/**
 * The shapes in any colours: `fill` for the square, `line` for its rule and
 * shadow, `ink` for the S. Everything in colour uses the defaults, the
 * brand's, on a light ground so the black rule and shadow show; only the
 * mono lockups pass other colours.
 */
export const markShapes = ({ fill = "#5ad2f5", line = "#000000", ink = "#000000" } = {}) =>
  // The square's outer edge runs 2–54, the shadow 8 further on.
  `<rect x="10" y="10" width="52" height="52" rx="8" fill="${line}"/>` +
  `<rect x="4" y="4" width="48" height="48" rx="6" fill="${fill}" stroke="${line}" stroke-width="4"/>` +
  `<path fill="${ink}" d="${S_PATH}"/>`;

/** The mark in the brand's colours. */
export const MARK_SHAPES = markShapes();

/** The mark placed at (x, y) and scaled to `size`, inside a larger SVG; `colors` as markShapes takes them. */
export const markGroup = (x, y, size, colors) =>
  `<g transform="translate(${x} ${y}) scale(${size / MARK_GRID})">${colors ? markShapes(colors) : MARK_SHAPES}</g>`;

/** The mark as a standalone SVG document (the favicon). */
export const markSvg = (size = MARK_GRID) =>
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${MARK_GRID} ${MARK_GRID}" width="${size}" height="${size}" role="img" aria-label="Solstack">${MARK_SHAPES}</svg>`;
